import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const FAQ_ITEMS = [
  {
    question: 'Does West Berkshire\'s all-GREEN DfT rating block my pothole claim?',
    answer: 'No — but it changes your approach. West Berkshire is one of only a handful of councils in England rated GREEN on all four DfT scorecards, which strengthens their Section 58 defence at network level. Your claim still turns on the specific defect: whether it met intervention criteria, whether it was found in a safety inspection, and whether it was repaired within the council\'s own KPI timeframes — not on the colour of a district-wide scorecard.',
  },
  {
    question: 'What does all-GREEN actually mean for West Berkshire?',
    answer: 'GREEN means West Berkshire performs above the DfT threshold on overall condition, spend, and best practice. Its own transparency report shows 2–4% of roads in RED condition across every class, capital spend running at roughly double the DfT allocation, and 88–92% of maintenance classed as preventative. It does not mean zero potholes: the council filled an estimated 9,983 potholes across five years, including 3,048 in 2024/25 alone.',
  },
  {
    question: 'West Berkshire shows just 3% RED roads — can I still claim?',
    answer: 'Yes. RED is a network survey category, not a guarantee that no individual pothole was unreasonably left unrepaired. On 711km of unclassified roads, 3% RED still represents roughly 21km of residential network in the worst condition band. The council\'s own data shows pothole fills nearly tripled from 924 in 2021/22 to 3,048 in 2024/25. Reactive repairs, public reports, and inspection records for your specific street decide the claim.',
  },
  {
    question: 'Why did pothole repairs nearly triple if the council is all-GREEN?',
    answer: 'West Berkshire\'s transparency report does not explain the rise directly, but it states potholes remain the defect most reported through the customer system and that the maintenance programme prioritises A roads first, then B, C, then other roads. A well-maintained network at district level can still produce rising reactive workload — particularly on the 711km of U-roads that make up 55% of the carriageway network. GREEN measures outcomes against other councils, not whether your specific defect was caught in time.',
  },
  {
    question: 'U-roads are surveyed 100% annually — does that weaken my claim?',
    answer: 'It strengthens the council\'s Section 58 position compared with councils on multi-year or alternate-year survey cycles. West Berkshire surveys its full unclassified network every year using walked inspections and mobile Route Reports — above the national minimum. Your claim therefore needs to show the specific defect should have been caught sooner: prior reports before your incident, photos showing visible age, or proof it met emergency or urgent KPI criteria and was not repaired within 2 or 24 hours.',
  },
  {
    question: 'What repair criteria does West Berkshire apply to reported potholes?',
    answer: 'The council\'s online guidance states it will normally repair potholes with a 40mm vertical edge and 300mm width, assessed using a risk-based approach — but each defect is judged individually. Emergency defects presenting immediate risk have a 98% on-time target within 2 hours; urgent 24-hour orders and routine 7- or 28-day orders have their own KPIs. Liability under Section 41 is not defined solely by the repair threshold — prior reports and evidence the pothole posed a hazard before your incident all matter.',
  },
  {
    question: 'Does the A–B–C–U road hierarchy affect claims on residential streets?',
    answer: 'Potentially, yes — as context, not as a legal excuse. West Berkshire\'s transparency report states funding is prioritised on A roads first, followed by B, C, then other roads, to focus on higher-risk routes. U-roads make up 711km — 55% of the network — and sit at the bottom of that programming hierarchy. If your pothole was on an estate or village road, ask whether condition data and inspection records for that street matched the council\'s published KPI targets for roads "in need of repair" (6% threshold on U-roads).',
  },
];

export function WestBerkshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="West Berkshire Pothole Claims | All-GREEN DfT Rating | Fixtyer"
        description="West Berkshire is one of England's few all-GREEN councils — yet filled 9,983 potholes in five years and nearly tripled reactive repairs by 2024/25. See what the transparency report reveals about your claim."
        canonical="/councils/west-berkshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'West Berkshire', url: '/councils/west-berkshire' },
        ]}
      />
      <FAQSchema faqs={FAQ_ITEMS} />
      <ServiceSchema
        councilName="West Berkshire Council"
        councilSlug="west-berkshire"
        rating="green"
      />
      <SpeakableSchema
        url="/councils/west-berkshire"
        headline="West Berkshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">West Berkshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="green" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            9,983 pothole repairs — on an all-GREEN council
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            West Berkshire Council is one of only a handful of authorities in England rated{' '}
            <strong className="text-emerald-400">GREEN on all four DfT scorecards</strong> — one of four in the
            South East. Its own transparency report shows capital spend at roughly double the DfT allocation,
            88–92% preventative maintenance, and just 2–4% of roads in RED condition. Yet the council still
            recorded an estimated <strong className="text-amber-400">9,983 pothole fills across five years</strong>,
            with reactive repairs nearly tripling from 924 in 2021/22 to 3,048 in 2024/25. GREEN is a network verdict.
            Your claim lives or dies on the specific defect.
          </p>

          <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-emerald-400">3,048</div>
            <div className="text-white font-medium mt-2">Pothole fills in 2024/25 alone</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 924 in 2021/22 — roughly eight repairs every day — on a 1,303km network where the council
              surveys 100% of unclassified roads annually and targets 91% preventative spend in 2025/26.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT Scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-8">
            The DfT&apos;s verdict — all four GREEN
          </h2>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full">
              <caption className="sr-only">West Berkshire Council DfT Road Maintenance Ratings 2025-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Scorecard</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Performance</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall</td>
                  <td className="px-6 py-4 text-center text-surface-600">—</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Condition</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">Above DfT threshold</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">76% green (2024 survey)</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">B &amp; C roads</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">75% green (2024 survey)</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Unclassified roads</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">3% red (2024 survey)</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center text-surface-600">~2× DfT capital allocation (2024/25)</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Best practice</td>
                  <td className="px-6 py-4 text-center text-surface-600">WMHI aligned, HIAMP, HMEP embedded</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <p className="text-emerald-800">
              <strong>What all-GREEN means:</strong> West Berkshire is performing above average on every DfT scorecard —
              one of 16 green-rated authorities in England and one of only four in the South East, alongside Portsmouth,
              Rotherham and others. The council states its ambition is to reach the top 10% of all local authorities for
              every road category within five years.
            </p>
          </div>

          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-amber-800">
              <strong>Context for your claim:</strong> GREEN measures network-level performance against other councils —
              not whether the specific pothole that damaged your vehicle was reasonably inspected and repaired. West
              Berkshire still filled an estimated 9,983 potholes across five years and documents that potholes remain
              the defect most reported through its customer system. A top rating makes generic &ldquo;the roads are
              terrible&rdquo; arguments useless. Specificity wins.
            </p>
          </div>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            1,303 kilometres — mostly unclassified
          </h2>
          <p className="text-surface-600 mb-8">
            Network size from West Berkshire&apos;s own transparency report — £2.96 billion gross replacement cost
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Length</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Share of network</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center font-mono">117 km</td>
                  <td className="px-6 py-4 text-center">9.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center font-mono">475 km</td>
                  <td className="px-6 py-4 text-center">36.5%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-medium text-amber-900">Unclassified (U roads)</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">711 km</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-700">54.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-bold text-brand-900">Total roads</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">1,303 km</td>
                  <td className="px-6 py-4 text-center">100%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Footways</td>
                  <td className="px-6 py-4 text-center font-mono">1,193 km</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-brand-900">Cycleways</td>
                  <td className="px-6 py-4 text-center font-mono">822 km</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Annual U-road surveys</h3>
              <p className="text-surface-600 text-sm mb-3">
                Unlike many authorities, West Berkshire surveys 100% of its unclassified network annually —
                above the national minimum. The council uses walked inspections by independent surveyors, mobile
                Route Reports that produce RAG-rated defect maps, and regular safety inspections by highway inspectors.
              </p>
              <p className="text-surface-600 text-sm">
                That is a stronger condition-monitoring regime than alternate-year or three-year cycles seen elsewhere —
                which raises the bar for your claim unless you can show prior notice or a defect that should have been
                caught sooner.
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">Road hierarchy in programming</h3>
              <p className="text-emerald-800 text-sm mb-3">
                The council&apos;s transparency report states maintenance is prioritised on A roads first, followed by
                B, C, then other roads — ensuring funding targets higher-risk routes. Reprioritisation during the year
                redirects spend to the worst-condition sections.
              </p>
              <p className="text-emerald-800 text-sm">
                For claims on U-roads — where most motorists actually drive — ask whether your street&apos;s condition
                data and inspection records matched the council&apos;s published KPI of no more than 6% of U-roads
                &ldquo;in need of repair&rdquo;.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five years of condition data
          </h2>
          <p className="text-surface-600 mb-8">
            RED / amber / green percentages from West Berkshire&apos;s transparency report — SCANNER on classified
            roads, annual surveys on unclassified
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (117km — 9% of network): stable, low RED</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">74%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">27%</td>
                    <td className="px-4 py-2 text-center">70%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">26%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4%</td>
                    <td className="px-4 py-2 text-center font-bold">20%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">76%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED has doubled from 2% to 4% since 2020, but green condition improved to 76% by 2024 as amber
              fell from 27% to 20%. The council&apos;s internal KPI target is no more than 4% of A-roads in need of repair.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-emerald-900 mb-4">B and C roads (475km — 36.5% of network): consistently strong</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-emerald-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-emerald-100 border-b border-emerald-200">
                    <th className="px-4 py-2 text-left font-semibold text-emerald-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">22%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">77%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">78%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">24%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">3%</td>
                    <td className="px-4 py-2 text-center font-bold">22%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">75%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-emerald-800 font-medium">
              B/C roads have held 75–78% green condition across five years. Amber peaked at 24% in 2023 — roughly
              114km where &ldquo;maintenance may be required soon&rdquo; — before easing to 22% in 2024.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">U-roads (711km — 55% of network): low RED, rising slowly</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">3%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 text-sm">
              U-road RED rose from 2% to 3% between 2020 and 2024 — on 711km that is roughly 14km to 21km of residential
              and village roads in the worst condition band. The council&apos;s KPI target allows up to 6% of U-roads in
              need of repair. From 2026/27, PAS 2161 will replace the three-category SCANNER system nationally.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">What GREEN condition data still shows</h3>
            <p className="text-brand-200 mb-4">
              Even England&apos;s best-rated councils carry defects. West Berkshire&apos;s own figures show:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• A-road RED doubled (2% → 4%) while the council targets no more than 4% in need of repair</li>
              <li>• B/C amber hit 24% in 2023 — over 100km flagged for maintenance soon</li>
              <li>• U-road RED rose 50% proportionally (2% → 3%) on the road class where most claims happen</li>
              <li>• Pothole fills nearly tripled in three years despite preventative-first asset management</li>
            </ul>
            <p className="text-white font-medium">
              Network-level GREEN does not mean your specific pothole was reasonably unknown or unforeseeable.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            9,983 estimated pothole fills in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair totals from the council&apos;s transparency report — the defect most reported by residents
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Estimated potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">1,072</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">924</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,011</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">2,928</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">3,048</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">9,983</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The tripling trend</h3>
              <p className="text-surface-600 text-sm">
                Pothole fills fell to 924 in 2021/22 — the low point — then rose every year to 3,048 in 2024/25.
                That is a 3.3× increase in three years on a network the DfT rates GREEN for condition. Averaged over
                five years, West Berkshire fills roughly five to six potholes every day — rising to about eight per day
                in 2024/25.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Customer reporting and inspections</h3>
              <p className="text-amber-800 text-sm">
                The council states potholes remain the defect most reported through its customer reporting system, and
                that regular highway inspections also identify safety repairs. An all-GREEN authority still processes
                thousands of reactive orders because defects form between surveys — which is when prior-report evidence
                decides claims.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">Following the money</h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            Capital spend consistently exceeds DfT allocation — with 88–92% classed as preventative maintenance
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 p-6 mb-8 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative (est.)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr>
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£7.243m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">£12.416m</td>
                  <td className="px-4 py-3 text-center">£3.286m</td>
                  <td className="px-4 py-3 text-center">92%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£5.387m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">£10.518m</td>
                  <td className="px-4 py-3 text-center">£2.762m</td>
                  <td className="px-4 py-3 text-center">90%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£5.387m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">£8.482m</td>
                  <td className="px-4 py-3 text-center">£2.789m</td>
                  <td className="px-4 py-3 text-center">88%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£5.304m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">£11.869m</td>
                  <td className="px-4 py-3 text-center">£2.533m</td>
                  <td className="px-4 py-3 text-center">89%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£5.387m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">£11.515m</td>
                  <td className="px-4 py-3 text-center">£2.348m</td>
                  <td className="px-4 py-3 text-center">88%</td>
                </tr>
                <tr className="bg-brand-800/50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center">£8.901m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">£16.801m</td>
                  <td className="px-4 py-3 text-center">£2.330m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">91%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid sm:grid-cols-3 gap-6 mb-6">
            <div>
              <div className="text-3xl font-bold text-emerald-400">2.1×</div>
              <div className="text-brand-300 text-sm">Capital spend vs DfT allocation (2024/25)</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-400">£14.5m</div>
              <div className="text-brand-300 text-sm">Average annual capital + revenue spend since 2020/21</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-400">0.49%</div>
              <div className="text-brand-300 text-sm">Of £2.96bn asset value spent on maintenance annually</div>
            </div>
          </div>

          <p className="text-xl font-semibold text-white">
            West Berkshire invests well beyond its DfT grant and targets a{' '}
            <span className="text-emerald-400">preventative-first approach</span> — yet still filled nearly 10,000
            potholes in five years. Spend scorecards measure programme quality, not whether the defect that damaged
            your car was reasonably inspected and repaired.
          </p>
        </div>
      </section>

      {/* KPIs and response times */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Response-time KPIs — the Section 58 detail
          </h2>
          <p className="text-surface-600 mb-8">
            Contractual targets from West Berkshire&apos;s term maintenance contract — where individual claims are won or lost
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Defect type</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Response target</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">On-time target</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Emergency (2hr orders)</td>
                  <td className="px-6 py-4 text-center">Immediate risk — dangerous pothole</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">98%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Urgent (24hr orders)</td>
                  <td className="px-6 py-4 text-center">High-traffic hazard, not yet immediately dangerous</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">98%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Routine (7-day orders)</td>
                  <td className="px-6 py-4 text-center">Non safety-critical, minimal traffic management</td>
                  <td className="px-6 py-4 text-center font-bold">95%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-brand-900">Routine (28-day orders)</td>
                  <td className="px-6 py-4 text-center">Requires planning or traffic management</td>
                  <td className="px-6 py-4 text-center font-bold">95%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Potholes remain as the defect that is most reported through our Council's customer reporting system. In addition to customer reporting, we also carry out regular inspections on the highway to identify any safety repairs that are needed."
            source="West Berkshire Council — Local Highways Maintenance Transparency Report, October 2025"
          />
        </div>
      </section>

      {/* 2025/26 plans */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            2025/26 programme — 64km of resurfacing
          </h2>
          <p className="text-surface-600 mb-8">
            Planned works from the council&apos;s transparency report — £12m network investment, £4m above 2024/25
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Carriageway programme</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• 83 roads identified for resurfacing — estimated 64km</li>
                <li>• Three-year highway improvement programme reviewed annually</li>
                <li>• Treatments include surface dressing, micro asphalt, slurry and inlay</li>
                <li>• £12m total network budget for 2025/26</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Footways and innovation</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• 18 footway schemes — approximately 6km of surface improvement</li>
                <li>• Low-temperature asphalt standard for resurfacing (lower carbon)</li>
                <li>• Route Reports and AI for road marking and sign asset management</li>
                <li>• Lane rental feasibility study for streetworks disruption reduction</li>
              </ul>
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
            <p className="text-emerald-800 text-sm">
              <strong>AONB context:</strong> Much of West Berkshire is designated as an Area of Outstanding Natural Beauty.
              The council documents balancing maintenance with environmental protection — using warm-mix asphalt, recycled
              materials, and rejuvenators. If your incident was on a rural lane in the AONB, treatment choices and drainage
              investigations may be relevant background — but they do not remove the council&apos;s statutory duty under
              Section 41.
            </p>
          </div>
        </div>
      </section>

      {/* Balanced assessment */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against an all-GREEN council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: West Berkshire is not Derbyshire — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> All four DfT scorecards GREEN — among England&apos;s best-rated authorities
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 100% annual U-road surveys — stronger than alternate-year cycles elsewhere
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 88–92% preventative spend with WMHI and HIAMP-aligned asset management
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 2–4% RED across all road classes — consistently low network deterioration
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Contractual KPIs with 98% emergency and urgent on-time targets
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence. Generic &ldquo;council neglect&rdquo; arguments will fail.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 9,983 estimated pothole fills in five years — defects still form
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Reactive fills nearly tripled from 924 to 3,048 in three years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road RED rose from 2% to 3% — roughly 21km in worst condition band
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> A–B–C–U hierarchy puts 711km of U-roads last in programming priority
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council states potholes are the most-reported defect — prior reports create dated notice
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with all-GREEN scorecards and annual U-road surveys, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong> under Section 41 and Section 58:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — online form at westberks.gov.uk/reportapothole or 01635 519080</li>
              <li>• Photos showing defect size, depth, and visible age (weathered edges, previous patching)</li>
              <li>• Whether the pothole met the council&apos;s 40mm / 300mm repair threshold or emergency KPI criteria</li>
              <li>• Road class — U-roads sit at the bottom of the A–B–C–U programming hierarchy</li>
              <li>• Whether the council met its own 2-hour, 24-hour, or 7-day contractual response targets</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and West Berkshire&apos;s own
              transparency data cited where it helps — without pretending an all-GREEN authority is the same as a
              failing one.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to West Berkshire first
          </h2>
          <p className="text-surface-600 mb-6">
            West Berkshire&apos;s transparency report states potholes are the defect most reported through its customer
            system. The council normally repairs potholes with a 40mm vertical edge and 300mm width, assessed using
            a risk-based approach. Reporting creates a dated case reference and email updates — useful evidence if
            the pothole was known before your incident, or if the council failed to repair it within its KPI timeframes.
          </p>
          <a
            href="https://citizen.westberks.gov.uk/reportapothole"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to West Berkshire Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            For emergencies presenting imminent threat to life or serious injury, call 01635 519080 instead of using
            the online form. Keep your case reference number and confirmation emails.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in West Berkshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            An all-GREEN council demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• Ignore all-GREEN Section 58 strength</li>
                <li>• No KPI response-time argument</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ All-GREEN rating acknowledged honestly</li>
                <li>✅ 9,983 five-year pothole total cited</li>
                <li>✅ U-road hierarchy and KPI timeframes argued</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to West Berkshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=west-berkshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my West Berkshire claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {FAQ_ITEMS.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.westberks.gov.uk/media/63889/Highways-Maintenance-Transparency-Report-2025/pdf/20250625-Highways_maintenance_transparency_report_A4.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              West Berkshire Council Local Highways Maintenance Transparency Report (October 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
